/* ---------- Regelvorlagen (0.63) ---------- */
/*
 * Vorlagen sind normale, editierbare Regeln ohne Sonderlogik: Sie füllen den Editor vor. Spalten, Labels und
 * Felder werden nur als Vorschlag über den Namen gesucht; was fehlt, bleibt leer und wird im Editor gewählt
 * (die Regel lässt sich erst speichern, wenn alle Verweise gültig sind).
 */
import type { AutomationAction, AutomationPredicate, AutomationTrigger, ConditionGroup } from './automation.js';

export interface TemplateBoard {
  columns: { id: string; title: string }[];
  labels: { id: string; name: string }[];
  fields: { id: string; name: string; type: string; options: { id: string; label: string }[] }[];
}

export interface RuleDraft {
  name: string;
  trigger: AutomationTrigger;
  when: ConditionGroup;
  actions: AutomationAction[];
  once?: boolean;
}

export interface RuleTemplate {
  id: string;
  name: string;
  description: string;
  build: (b: TemplateBoard) => { draft: RuleDraft; missing: string[] };
}

const norm = (s: string) => s.trim().toLocaleLowerCase('de');
const find = <T extends { id: string }>(list: T[], name: (x: T) => string, words: string[]) =>
  list.find((x) => words.some((w) => norm(name(x)) === w)) ?? list.find((x) => words.some((w) => norm(name(x)).includes(w)));

export const RULE_TEMPLATES: RuleTemplate[] = [
  {
    id: 'pruefbereit',
    name: 'Prüfbereit',
    description: 'Sobald eine offene Karte in „Prüfung“ mindestens eine PDF-Datei hat: Label „Prüfbereit“.',
    build: (b) => {
      const col = find(b.columns, (c) => c.title, ['prüfung', 'review', 'freigabe']);
      const lab = find(b.labels, (l) => l.name, ['prüfbereit']);
      const missing = [...(col ? [] : ['Spalte für die Prüfung']), ...(lab ? [] : ['Label „Prüfbereit“ (vorher unter Labels anlegen)'])];
      return {
        missing,
        draft: {
          name: 'Prüfbereit',
          trigger: { type: 'stateMet' },
          when: {
            op: 'all',
            items: [
              { type: 'done', value: false },
              ...(col ? [{ type: 'column', mode: 'in', columnIds: [col.id] } as AutomationPredicate] : []),
              { type: 'files', kind: 'pdf', includeComments: false, min: 1 },
            ],
          },
          actions: [{ type: 'labels', add: lab ? [lab.id] : [], remove: [] }],
        },
      };
    },
  },
  {
    id: 'eilauftrag',
    name: 'Eilauftrag',
    description: 'Wenn „Dringend“ hinzugefügt wird und niemand zuständig ist: zuweisen und Priorität hoch.',
    build: (b) => {
      const lab = find(b.labels, (l) => l.name, ['dringend', 'eilig', 'urgent']);
      return {
        missing: lab ? [] : ['Label „Dringend“ (vorher anlegen)'],
        draft: {
          name: 'Eilauftrag',
          trigger: { type: 'labelAdded', labelIds: lab ? [lab.id] : [] },
          when: { op: 'all', items: [{ type: 'assignees', mode: 'none' }] },
          actions: [{ type: 'assign', userId: 'actor' }, { type: 'setPriority', value: 3 }],
        },
      };
    },
  },
  {
    id: 'start-dokumentieren',
    name: 'Start dokumentieren',
    description: 'Wenn eine Karte nach „In Arbeit“ kommt und der Start fehlt: Start auf diesen Zeitpunkt setzen.',
    build: (b) => {
      const col = find(b.columns, (c) => c.title, ['in arbeit', 'in bearbeitung', 'doing', 'arbeit']);
      return {
        missing: col ? [] : ['Spalte „In Arbeit“'],
        draft: {
          name: 'Start dokumentieren',
          trigger: { type: 'moved', columnId: col?.id ?? b.columns[1]?.id ?? b.columns[0]?.id ?? '' },
          when: { op: 'all', items: [{ type: 'dates', which: 'start', op: 'missing' }] },
          actions: [{ type: 'setDate', which: 'start', base: 'event', amount: 0, unit: 'days', time: 'base', onlyIfEmpty: true }],
        },
      };
    },
  },
  {
    id: 'frist-ab-start',
    name: 'Frist ab Start',
    description: 'Wenn der Start gesetzt wird und die Frist fehlt: Frist drei Kalendertage nach dem Start, 17:00 Uhr.',
    build: () => ({
      missing: [],
      draft: {
        name: 'Frist ab Start',
        trigger: { type: 'dateChanged', which: 'start', change: 'set' },
        when: { op: 'all', items: [{ type: 'dates', which: 'due', op: 'missing' }] },
        actions: [{ type: 'setDate', which: 'due', base: 'start', amount: 3, unit: 'days', time: '17:00', onlyIfEmpty: true }],
      },
    }),
  },
  {
    id: 'aufwandswarnung',
    name: 'Aufwandswarnung',
    description: 'Sobald 80 % der Schätzung gebucht sind (mit laufenden Timern) und die Karte offen ist: Label „Budget prüfen“.',
    build: (b) => {
      const lab = find(b.labels, (l) => l.name, ['budget prüfen', 'budget']);
      return {
        missing: lab ? [] : ['Label „Budget prüfen“ (vorher anlegen)'],
        draft: {
          name: 'Aufwandswarnung',
          trigger: { type: 'stateMet' },
          when: {
            op: 'all',
            items: [
              { type: 'effort', measure: 'percent', op: 'gte', value: 80, includeRunning: true },
              { type: 'done', value: false },
            ],
          },
          actions: [{ type: 'labels', add: lab ? [lab.id] : [], remove: [] }],
        },
      };
    },
  },
  {
    id: 'freigabe-feld',
    name: 'Freigabe nach Feld',
    description: 'Wenn das Auswahlfeld „Freigabe“ auf „Genehmigt“ wechselt und eine Datei vorhanden ist: nach „Freigegeben“ verschieben.',
    build: (b) => {
      const f = b.fields.find((x) => x.type === 'select' && norm(x.name).includes('freigabe')) ?? b.fields.find((x) => x.type === 'select');
      const opt = f?.options.find((o) => ['genehmigt', 'freigegeben', 'ok'].some((w) => norm(o.label).includes(w)));
      const col = find(b.columns, (c) => c.title, ['freigegeben', 'erledigt', 'fertig', 'done']);
      return {
        missing: [...(f ? [] : ['Auswahlfeld „Freigabe“ (vorher unter Felder anlegen)']), ...(opt ? [] : ['Wert „Genehmigt“']), ...(col ? [] : ['Spalte „Freigegeben“'])],
        draft: {
          name: 'Freigabe nach Feld',
          trigger: { type: 'fieldChanged', fieldId: f?.id ?? '', ...(opt ? { to: { op: 'anyOf' as const, optionIds: [opt.id] } } : {}) },
          when: { op: 'all', items: [{ type: 'files', kind: 'any', includeComments: false, min: 1 }] },
          actions: [{ type: 'move', columnId: col?.id ?? b.columns.at(-1)?.id ?? '', placement: 'top' }],
        },
      };
    },
  },
  {
    id: 'start-ohne-zustaendige',
    name: 'Start ohne Zuständige',
    description: 'Sobald der Start erreicht ist, die Karte offen ist und niemand zuständig ist: Hauptverantwortliche und Beobachtende benachrichtigen.',
    build: () => ({
      missing: [],
      draft: {
        name: 'Start ohne Zuständige',
        trigger: { type: 'stateMet' },
        when: {
          op: 'all',
          items: [
            { type: 'dateWindow', which: 'start', op: 'reached' },
            { type: 'assignees', mode: 'none' },
            { type: 'done', value: false },
          ],
        },
        actions: [{ type: 'notify', text: 'Der Start ist erreicht, aber niemand ist zuständig.', recipients: { owner: true, watchers: true, users: [], groups: [] } }],
      },
    }),
  },
  {
    id: 'uebergabe',
    name: 'Übergabe nach Vorgängerabschluss',
    description: 'Wenn eine Karte startbereit wird: die Zuständigen benachrichtigen.',
    build: () => ({
      missing: [],
      draft: {
        name: 'Übergabe nach Vorgängerabschluss',
        trigger: { type: 'ready' },
        when: { op: 'all', items: [] },
        actions: [{ type: 'notify', text: 'Du kannst starten – alle Vorgänger sind erledigt.', recipients: { assignees: true, users: [], groups: [] } }],
      },
    }),
  },
];
